import { COST_NAV_SECTIONS, type CostNavItem, type CostNavSection } from "./cost-library-nav";
import { getCostTaskNavLocation, type CostTaskNavInput, type CostNavLocation } from "./cost-library-labels";

/** Browsing is membership, not a single best-label classification. */
export function matchesCostNavItem(task: CostTaskNavInput, item: CostNavItem): boolean {
  return [item, ...(item.browseFilters ?? [])].some(filter => {
    if (filter.categoryFilter !== undefined && task.category !== filter.categoryFilter) return false;
    if (filter.tradeFilter !== undefined && task.trade !== filter.tradeFilter) return false;
    if (item.browseFilter) {
      const fields = [task.name, task.description ?? "", task.keywords ?? ""]
        .map(text => ` ${normaliseEvidence(text)} `);
      const contains = (field: string, phrase: string) => {
        const normalised = normaliseEvidence(phrase);
        return Boolean(normalised) && field.includes(` ${normalised} `);
      };
      const { nameAnyOf, allOf } = item.browseFilter;
      if (nameAnyOf && !nameAnyOf.some(phrase => contains(fields[0]!, phrase))) return false;
      return allOf.every(group => group.some(phrase => fields.some(field => contains(field, phrase))));
    }
    const words = filter.keywordFilter?.toLowerCase().split(/\s+/).filter(Boolean) ?? [];
    const text = `${task.name} ${task.description ?? ""} ${task.keywords ?? ""}`.toLowerCase();
    return !words.length || words.some(word => text.includes(word));
  });
}

export function matchesCostNavSection(task: CostTaskNavInput, section: CostNavSection): boolean {
  const categories = new Set(section.items.flatMap(item =>
    [item.categoryFilter, ...(item.browseFilters ?? []).map(filter => filter.categoryFilter)],
  ).filter(category => category !== undefined));
  // Preserve the library's category-wide parent view when it has one category.
  if (categories.size === 1) return categories.has(task.category);
  // Mixed-category parents must not accidentally search the entire catalogue.
  return section.items.some(item => matchesCostNavItem(task, item));
}

export function getCostTaskBrowseMemberships(task: CostTaskNavInput, sections: readonly CostNavSection[] = COST_NAV_SECTIONS) {
  return sections
    .filter(section => matchesCostNavSection(task, section))
    .map(section => ({
      section,
      items: section.items.filter(item => matchesCostNavItem(task, item)),
    }));
}

/** A browse link must land on a leaf that actually contains the task.
 * Preserve the legacy label preference where valid, without changing it. */
export function getCostTaskBrowseLocation(task: CostTaskNavInput, sections: readonly CostNavSection[] = COST_NAV_SECTIONS): CostNavLocation | null {
  const preferred = getCostTaskNavLocation(task);
  if (preferred && matchesCostNavItem(task, preferred.item)) return preferred;
  for (const { section, items } of getCostTaskBrowseMemberships(task, sections)) {
    if (items[0]) return { section, item: items[0] };
  }
  return null;
}

function normaliseEvidence(text: string): string {
  return text.toLowerCase().replace(/[_\W]+/g, " ").trim().replace(/\s+/g, " ");
}
